import { History } from "lucide-react";
import { useState } from "react";
import { useGetList } from "ra-core";
import { cn } from "@/lib/utils";

import { CallSummaryButton } from "../ai/CallSummaryButton";

import { EmptyState } from "../data-display/EmptyState";
import { StatusBadge } from "../data-display/StatusBadge";
import {
  TIMELINE_FILTERS,
  TIMELINE_STATUS_TONES,
  type TimelineEntry,
  formatWhen,
  timelineMeta,
} from "./timelineModel";

/**
 * One honest history for a customer: every call, text, email, note, task,
 * appointment, estimate, invoice, payment and job, newest first. Reads the
 * `contact_timeline` view (security_invoker), so a row only appears if the
 * viewer could already read the underlying record.
 */
export const ContactTimeline = ({
  contactId,
  className,
}: {
  contactId: number | string;
  className?: string;
}) => {
  const [filter, setFilter] = useState<string>("all");

  const { data: entries, isPending } = useGetList<TimelineEntry>(
    "contact_timeline",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "occurred_at", order: "DESC" },
      filter: { contact_id: contactId },
    },
  );

  const active = TIMELINE_FILTERS.find((f) => f.value === filter);
  const visible = (entries ?? []).filter(
    (entry) => !active?.kinds.length || active.kinds.includes(entry.kind),
  );

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {TIMELINE_FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFilter(option.value)}
            className={cn(
              "shrink-0 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors",
              filter === option.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary/50",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {isPending ? null : visible.length === 0 ? (
        <div className="py-8">
          <EmptyState
            icon={History}
            title="Nothing here yet"
            description="Calls, messages, notes and work for this customer will appear here."
          />
        </div>
      ) : (
        <ol className="flex flex-col">
          {visible.map((entry, index) => {
            const meta = timelineMeta(entry.kind);
            const Icon = meta.icon;
            return (
              <li key={entry.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={cn(
                      "flex size-7 shrink-0 items-center justify-center rounded-full",
                      meta.chip,
                    )}
                  >
                    <Icon className="size-3.5" strokeWidth={1.9} />
                  </span>
                  {index < visible.length - 1 && (
                    <span className="w-px flex-1 bg-border" aria-hidden />
                  )}
                </div>
                <div className="min-w-0 flex-1 pb-4">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-[12px] font-semibold text-foreground">
                      {meta.label}
                      {entry.subkind ? (
                        <span className="ml-1 font-normal text-muted-foreground">
                          · {entry.subkind}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-[11.5px] text-muted-foreground">
                      {formatWhen(entry.occurred_at)}
                    </span>
                    {entry.status ? (
                      <StatusBadge
                        label={entry.status.replace(/_/g, " ")}
                        tone={TIMELINE_STATUS_TONES[entry.status] ?? "neutral"}
                      />
                    ) : null}
                  </div>
                  {entry.title ? (
                    <p className="mt-0.5 break-words text-[13px] text-foreground">
                      {entry.title}
                    </p>
                  ) : null}
                  {entry.detail && entry.detail !== entry.title ? (
                    <p className="mt-0.5 break-words text-[12.5px] text-muted-foreground">
                      {entry.detail}
                    </p>
                  ) : null}
                  {entry.kind === "call" ? (
                    // Timeline ids are prefixed per source ("call:<uuid>").
                    <CallSummaryButton
                      callId={entry.id.replace(/^call:/, "")}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
